import React, { useEffect, useState } from "react";
import { fetchActivity, type ActivityInfo } from "../../../../api/peContent";
import type { Lang } from "../../../../api/serving";
import { peT } from "../../../../i18n/peLesson";
import { peActivitiesEs } from "../../../../data/peActivitiesEs";
import { getActivityStudentText } from "../../../../data/peActivityStudentText";

/**
 * Run E2 J5: pe_activities is English only. In a Spanish lesson, show the
 * Spanish title/description from src/data/peActivitiesEs.ts (keyed by the
 * English title; the description only while the DB English still matches).
 */
function localizeActivity(a: ActivityInfo, lang: Lang): ActivityInfo {
  if (lang !== "es" || !a.title) return a;
  const es = peActivitiesEs[a.title.trim()];
  if (!es) return a;
  const sameDesc = (a.description ?? "").trim() === (es.description_en ?? "");
  return {
    title: es.title_es,
    description: sameDesc ? es.description_es : a.description,
  };
}

/**
 * Activity — step 4 (Independent / You Do). Student-facing view ONLY:
 * activity_title + activity_description + a short student framing. The teacher
 * facilitation markdown (variant_20/30/45, materials_*) is deliberately NOT
 * fetched or rendered here — it is teacher-only and lives in the Phase-6
 * doc-hub. No teacher directives ("tally on board", "pre-place objects") reach
 * the student. Completion is owned by LessonSequence's advance control.
 */
const ActivityStep: React.FC<{ chapterId: string; lang: Lang }> = ({ chapterId, lang }) => {
  const t = peT(lang);
  const [activity, setActivity] = useState<ActivityInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchActivity(chapterId)
      .then((a) => { if (!cancelled) setActivity(a); })
      .catch((e) => {
        console.warn("[ActivityStep] activity load failed:", e);
        if (!cancelled) setActivity(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [chapterId]);

  if (loading) {
    return (
      <div className="rounded-xl bg-white border border-gray-200 p-8 text-center text-gray-500">
        {t.loadingActivity}
      </div>
    );
  }

  if (!activity || (!activity.title && !activity.description)) {
    return (
      <div className="rounded-xl bg-white border border-gray-200 p-8 text-center">
        <h2 className="text-2xl font-bold text-gray-900">{t.activityTitle}</h2>
        <p className="mt-2 text-gray-500">{t.noActivity}</p>
      </div>
    );
  }

  const shown = localizeActivity(activity, lang);
  // Run CT: never show the DB/teacher-voice description to students; show the
  // student-voice text (keyed by the English title) or nothing.
  const studentText = getActivityStudentText(activity.title, lang);
  return (
    <section className="rounded-xl bg-white border border-gray-200 p-8">
      <p className="text-sm font-medium uppercase tracking-wide text-gray-400">
        {t.yourTurn}
      </p>
      <h2 className="mt-1 text-2xl font-bold text-gray-900">
        {shown.title ?? t.activityTitle}
      </h2>
      {studentText && (
        <p className="mt-3 text-gray-800 leading-relaxed max-w-prose">
          {studentText}
        </p>
      )}
      <p className="mt-6 text-gray-600">{t.activityHint(t.markComplete)}</p>
    </section>
  );
};

export default ActivityStep;
